if (!customElements.get('post-atc-upsell')) {
  customElements.define(
    'post-atc-upsell',
    class extends HTMLElement {
      async connectedCallback() {
        this.dialog = this.querySelector('dialog');
        if (!this.dialog) return;

        this.triggerVariantIds = new Set(
          (this.dataset.triggerVariantIds || '')
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean)
        );
        this.offerVariantId = this.dataset.offerVariantId;
        this.oncePerSession = this.dataset.oncePerSession === 'true';
        this.sessionKey = `bliss:post-atc-upsell:${this.offerVariantId}`;

        if (!this.triggerVariantIds.size || !this.offerVariantId) return;

        this.acceptButton = this.querySelector('[data-post-atc-upsell-accept]');
        this.errorEl = this.querySelector('[data-post-atc-upsell-error]');

        this.countdownEl = this.querySelector('[data-post-atc-upsell-countdown]');
        this.timerLabelEl = this.querySelector('[data-post-atc-upsell-timer-label]');
        this.barEl = this.querySelector('[data-post-atc-upsell-bar]');
        this.timerSeconds = parseInt(this.dataset.timerSeconds, 10) || 0;
        this.timerExtendedLabel = this.dataset.timerExtendedLabel || '';

        this.acceptButton?.addEventListener('click', this.accept.bind(this));
        this.querySelectorAll('[data-post-atc-upsell-decline]').forEach((button) => {
          button.addEventListener('click', () => this.dialog.close());
        });

        // Clicking the backdrop lands on the dialog itself, not on its contents.
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        });

        // Single exit point: Escape, the backdrop, and both decline buttons all
        // end here, so the held-back cart is released in exactly one place.
        // showModal() gives Escape, the focus trap, and focus restore to the
        // Add-to-cart button for free - none of that is reimplemented.
        this.dialog.addEventListener('close', () => {
          this.stopCountdown();
          if (!this.releaseCart) return;
          this.releaseCart = false;
          this.openCart();
        });

        // Patch before subscribing, so the very first add is held rather than
        // slipping through while the drawer element is still being upgraded.
        await this.patchCartOpen();

        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      }

      disconnectedCallback() {
        this.unsubscribe?.();
        this.stopCountdown();
      }

      // product-form.js calls cart.renderContents() right after publishing
      // cartUpdate, and renderContents() opens the drawer. Shadow open() on the
      // instance so a single add can be held back without touching cart-drawer.js.
      //
      // This snippet renders inside main-product, so its script tag sits ABOVE
      // cart-drawer.js in the document and therefore runs first. Waiting on the
      // definition keeps the patch working regardless of that order - without it,
      // this.cart.open is still undefined here and the hold silently no-ops.
      async patchCartOpen() {
        const tag = document.querySelector('cart-drawer')
          ? 'cart-drawer'
          : document.querySelector('cart-notification')
            ? 'cart-notification'
            : null;
        if (!tag) return;

        await customElements.whenDefined(tag);

        const cart = document.querySelector(tag);
        if (!cart || typeof cart.open !== 'function') return;

        this.cart = cart;
        this.openCartNative = cart.open.bind(cart);
        cart.open = (triggeredBy) => {
          if (this.holdCart) return;
          this.openCartNative(triggeredBy);
        };
      }

      openCart() {
        this.holdCart = false;
        this.openCartNative?.();
      }

      onCartUpdate(event) {
        if (!event || event.source === 'post-atc-upsell') return;
        if (!this.triggerVariantIds.has(String(event.productVariantId))) return;
        if (this.oncePerSession && this.readSessionFlag()) return;

        // The drawer being open already means the add came from inside it (the
        // in-drawer upsell card). Nothing to hold back - layer the popup on top.
        const cartAlreadyOpen = this.cart?.classList.contains('active');
        if (this.openCartNative && !cartAlreadyOpen) {
          this.holdCart = true;
          this.releaseCart = true;
        }

        // Deciding whether to show needs current cart contents, which the add
        // response does not carry. Release the cart if that check rules us out.
        this.shouldOffer().then((show) => {
          if (show) {
            this.show();
          } else if (this.releaseCart) {
            this.releaseCart = false;
            this.openCart();
          }
        });
      }

      // Skip the offer when it is already in the cart - there is nothing to upsell.
      async shouldOffer() {
        try {
          const response = await fetch(`${routes.cart_url}.js`, {
            headers: { Accept: 'application/json' },
          });
          const cart = await response.json();
          return !(cart.items || []).some((item) => String(item.variant_id) === this.offerVariantId);
        } catch (error) {
          console.error('[post-atc-upsell] cart lookup failed', error);
          return false;
        }
      }

      show() {
        if (this.dialog.open) return;

        this.setError();
        if (this.oncePerSession) this.writeSessionFlag();

        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }

        this.startCountdown();
      }

      // Fake urgency: count down from the configured length. On reaching zero the
      // offer is never removed - swap to the "extended" label and restart, or
      // settle on 00:00 when no extended label is authored.
      startCountdown() {
        if (!this.countdownEl || !this.timerSeconds) return;
        this.stopCountdown();
        this.remaining = this.timerSeconds;
        this.renderCountdown();
        this.countdownId = setInterval(() => {
          this.remaining -= 1;
          if (this.remaining <= 0) {
            if (this.timerExtendedLabel) {
              if (this.timerLabelEl) this.timerLabelEl.textContent = this.timerExtendedLabel;
              this.remaining = this.timerSeconds;
              this.renderCountdown();
            } else {
              this.remaining = 0;
              this.renderCountdown();
              this.stopCountdown();
            }
            return;
          }
          this.renderCountdown();
        }, 1000);
      }

      renderCountdown() {
        const m = Math.floor(this.remaining / 60);
        const s = this.remaining % 60;
        this.countdownEl.textContent = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
        // Draining bar (barber style): width tracks the time remaining.
        if (this.barEl) {
          this.barEl.style.width = `${Math.max(0, (this.remaining / this.timerSeconds) * 100)}%`;
        }
      }

      stopCountdown() {
        if (this.countdownId) {
          clearInterval(this.countdownId);
          this.countdownId = null;
        }
      }

      async accept() {
        if (this.acceptButton.getAttribute('aria-disabled') === 'true') return;

        this.setError();
        this.toggleLoading(true);

        try {
          const response = await fetch(`${routes.cart_add_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [
                {
                  id: parseInt(this.offerVariantId, 10),
                  quantity: 1,
                  properties: { _source: 'Post ATC Upsell' },
                },
              ],
              sections: this.getCartSections(),
              sections_url: window.location.pathname,
            }),
          });
          const result = await response.json();

          if (result.status) throw new Error(result.description || result.message);

          // renderContents() opens the cart itself, so hand the release to it
          // rather than letting the close handler open it a second time.
          this.releaseCart = false;
          this.holdCart = false;
          this.dialog.close();

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'post-atc-upsell',
            productVariantId: this.offerVariantId,
            cartData: result,
          });

          if (this.cart) {
            this.cart.classList.remove('is-empty');
            this.cart.renderContents(result);
          }
        } catch (error) {
          console.error('[post-atc-upsell] add failed', error);
          this.setError(this.dataset.errorText);
        } finally {
          this.toggleLoading(false);
        }
      }

      getCartSections() {
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          return this.cart.getSectionsToRender().map((section) => section.id);
        }
        return ['cart-drawer', 'cart-icon-bubble'];
      }

      toggleLoading(loading) {
        this.acceptButton.classList.toggle('loading', loading);
        this.acceptButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
        this.dialog.toggleAttribute('aria-busy', loading);
        if (loading) {
          this.acceptButton.setAttribute('aria-disabled', 'true');
        } else {
          this.acceptButton.removeAttribute('aria-disabled');
        }
      }

      setError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || '';
        this.errorEl.toggleAttribute('hidden', !message);
      }

      readSessionFlag() {
        try {
          return sessionStorage.getItem(this.sessionKey) === 'true';
        } catch (error) {
          return false;
        }
      }

      writeSessionFlag() {
        try {
          sessionStorage.setItem(this.sessionKey, 'true');
        } catch (error) {
          // Private browsing can block sessionStorage - the popup just repeats.
        }
      }
    }
  );
}
